// Contexto puro do fluxo Normal (AD-002): funções de regra recebem `(ctx, task)` e
// devolvem valores — nada de React aqui. `NormalRuleContext` transporta o que era
// estado/memo do monólito (src/pages/MinhaEquipe/ApreciarRealizacao/index.tsx),
// com os MESMOS valores iniciais do useState correspondente para paridade estrita.

import { nivelApreciacao } from 'utils/nivelApreciacao';

import { EvaluationStep, PlayerFields, TaskRecord } from '../shared/types';

// Etapa (mês) de um ciclo, como chega em `cyclesExtended[].steps` / `stages`.
// Datas em epoch millis; `type` distingue month/trimester/semester (OM-03/COT-01).
export interface CycleStep {
  _id?: string;
  title?: string;
  type?: string;
  status?: string;
  date_start?: number;
  date_finish?: number;
  date_evaluation_step2_start?: number;
  date_evaluation_step2_finish?: number;
  date_evaluation_step3_start?: number;
  date_evaluation_step3_finish?: number;
  date_evaluation_step4_start?: number;
  date_evaluation_step4_finish?: number;
  date_evaluation_pitStop_start?: number;
  date_evaluation_pitStop_finish?: number;
  date_evaluation_step5_start?: number;
  date_evaluation_step5_finish?: number;
  date_trimester_start?: number;
  date_trimester_finish?: number;
  [key: string]: any;
}

// Ciclo estendido do useAuth (cyclesExtended), com seus steps internos.
export interface CycleExtended {
  _id: string;
  steps?: CycleStep[];
  date_start?: number;
  date_end?: number;
  [key: string]: any;
}

// Bônus "Dobra extra" carregado do backend (estado `bonus` do monólito).
export interface BonusInfo {
  hasBonus?: boolean;
  isUsed?: boolean;
  taskId?: string;
  stage?: number;
  action?: string;
  [key: string]: any;
}

export interface NormalRuleContext {
  // Gestor dono da sessão de apreciação (player_fields__c). No monólito é o
  // estado `playerInfo` (gestor substituído, quando em modo férias).
  playerInfo: PlayerFields | undefined;
  // Usuário logado (substituto em modo férias). Estado `userInfo` do useAuth.
  userInfo: PlayerFields | undefined;
  // Usuário interno exibido na página (estado `internalUser`) — só a presença é
  // consultada pelas regras (guard de isCurrentEvaluator).
  internalUser: { _id: string; [key: string]: any } | undefined;
  // Hora do SERVIDOR em epoch millis (estado `realDatetime`; 0 = ainda não carregada).
  realDatetime: number;
  // Etapa (mês) selecionada no ciclo (memo `activeStageObj`).
  activeStageObj: CycleStep | undefined;
  // Janela de apreciação ativa (memo `getActiveEvaluationStep`) — aqui como VALOR.
  getActiveEvaluationStep: EvaluationStep;
  // Ciclos estendidos do useAuth (inclui steps month/trimester/semester).
  cyclesExtended: CycleExtended[] | undefined;
  // Ciclos "reais" do useAuth (realCycle) — getTaskStatuses resolve o step da task
  // por eles, não por cyclesExtended (UI-08).
  realCycle: CycleExtended[] | undefined;
  // true quando o logado atua como substituto de férias de outro gestor.
  isVacationManager: boolean;
  // Cotas e universos calculados por fase (estados homônimos do monólito).
  doubledTasks: TaskRecord[];
  maxDoubledTasks: number;
  promotedTasks: TaskRecord[];
  maxPromotedTasks: number;
  // uau/mandouBem começam undefined no monólito (useState<any[]>() sem inicial) —
  // as regras testam Array.isArray, então o default preserva o comportamento.
  uauTasks: TaskRecord[] | undefined;
  maxUauTasks: number;
  mandouBemTasks: TaskRecord[] | undefined;
  maxMandouBemTasks: number;
  // Bônus "Dobra extra" (estado `bonus`).
  bonus: BonusInfo | undefined;
  // COT-06: o filtro de maxDoubledTasks usa a hora do CLIENTE (`+new Date()`),
  // não a do servidor — bug preservado por paridade e injetável para teste.
  clientNow: () => number;
}

export type NormalRuleContextInput = Partial<NormalRuleContext>;

// Monta o contexto com os mesmos defaults do estado React original.
export function buildNormalContext(input: NormalRuleContextInput = {}): NormalRuleContext {
  return {
    playerInfo: input.playerInfo,
    userInfo: input.userInfo,
    internalUser: input.internalUser,
    realDatetime: input.realDatetime ?? 0,
    activeStageObj: input.activeStageObj,
    getActiveEvaluationStep: input.getActiveEvaluationStep ?? null,
    cyclesExtended: input.cyclesExtended,
    realCycle: input.realCycle,
    isVacationManager: input.isVacationManager ?? false,
    doubledTasks: input.doubledTasks ?? [],
    maxDoubledTasks: input.maxDoubledTasks ?? 0,
    promotedTasks: input.promotedTasks ?? [],
    maxPromotedTasks: input.maxPromotedTasks ?? 0,
    uauTasks: input.uauTasks,
    maxUauTasks: input.maxUauTasks ?? 0,
    mandouBemTasks: input.mandouBemTasks,
    maxMandouBemTasks: input.maxMandouBemTasks ?? 0,
    bonus: input.bonus,
    clientNow: input.clientNow ?? (() => Date.now()),
  };
}

// Nível efetivo do gestor (NIV-01..05) — só pelo `cod_etapa`, via `utils/nivelApreciacao`.
// Regra redefinida em 2026-10-21 (Evolução PM 6ace7452): o `cod_etapa_sub` saiu do nível
// e vale apenas na busca lateral (`get_gestor_prefixo`); o substituto aprecia o próprio
// time pelo nível oficial e age como o nível que substitui pela lateralidade. Antes
// (NIV-04 de 2026-07-23, `ea64d0e7`) o sub vencia quando maior, e um cod 1 com sub 4
// virava Nível 2 no próprio time. Composição pela dupla MAD só com verifyMad=true
// (NIV-02/03) e também só por `cod_etapa` (A1). NIV-05: quando o valor PRÓPRIO vence,
// o retorno é CRU (cod_etapa 1 retorna 1, não 3; 99 retorna 99) — os call-sites testam
// `=== 1 || === 3`.
export function userCodEtapa(ctx: NormalRuleContext, userI?: any, verifyMad = false): number | undefined {
  if (!userI && !ctx.playerInfo) return undefined;

  return nivelApreciacao(userI ?? ctx.playerInfo, { comporDupla: verifyMad });
}
